import React, { useState, useEffect } from "react";
import { List } from "components";
import { Button } from "ui/components/button";
import { eventBus } from "utils/eventBus";
import {
  addItemsToCollection,
  deleteItemFromCollection,
} from "directus/src/operations";
import MultiFileInput from "components/file-upload/multiple-input";
import { invalidateAllQueries } from "utils/utils";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { assetUrl } from "utils/index";
import { screenshotsSchema } from "./schema";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { FormLabel } from "components/form";
import { useMemo } from "react";
import { getRequiredFields } from "components/form/utils";

export default function AssetListCrud({
  title = "Assets",
  storageKey,
  endPoint,
  entityId,
  relation = { entityField: "case_study", assetField: "screenshot" },
  onAfterChange,
  required = false,
}) {
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [listItems, setListItems] = useState([]);
  const itemsRef = React.useRef([]);

  const form = useForm({
    resolver: zodResolver(screenshotsSchema),
    defaultValues: {
      screenshots: [],
    },
  });

  const requiredFields = useMemo(
    () => (required ? getRequiredFields(screenshotsSchema) : []),
    [required]
  );

  // Update form validation based on list items
  useEffect(() => {
    if (!required) return;

    const hasScreenshots = listItems.length > 0;
    form.setValue("screenshots", hasScreenshots ? [{}] : []);

    if (!hasScreenshots) {
      form.setError("screenshots", {
        type: "manual",
        message: "This field is required",
      });
    } else {
      form.clearErrors("screenshots");
    }
  }, [listItems, required, form]);

  const fields = [
    "id",
    `${relation.entityField}.id`,
    `${relation.assetField}.id`,
  ];

  // Custom grid renderer similar to bookmarks card-renderer
  const GridRenderer = React.useCallback(({ items }) => {
    // Update parent state when items change (using ref to prevent infinite loops)
    if (JSON.stringify(itemsRef.current) !== JSON.stringify(items)) {
      itemsRef.current = items;
      setListItems(items || []);
    }

    const handleDelete = async (id) => {
      // If required and this is the last item, prevent deletion
      if (required && items.length === 1) {
        form.setError("screenshots", {
          type: "manual",
          message: "At least one screenshot is required",
        });
        return;
      }

      try {
        await deleteItemFromCollection(endPoint, id);
        onAfterChange?.();
        invalidateAllQueries();
        eventBus.emit("refresh:list", `${endPoint}${storageKey}`);
      } catch (error) {
        console.error("Error deleting screenshot:", error);
      }
    };

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 py-4 px-1.5">
        {items.map((item) => {
          const assetId = item?.[relation.assetField]?.id;
          const url = assetUrl(assetId);

          return (
            <div
              key={item.id}
              className="group relative border rounded-md overflow-hidden cursor-pointer"
              onClick={() => {
                if (url) {
                  window.open(url, "_blank");
                }
              }}
            >
              <div className="aspect-square bg-muted flex items-center justify-center">
                {url ? (
                  <img src={url} alt={assetId} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-sm text-gray-500">No image</div>
                )}
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <Button
                  size="icon"
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                >
                  ✕
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [required, endPoint, storageKey, relation, form, onAfterChange]);

  const filters = {
    [relation.entityField]: {
      id: { _eq: entityId },
    },
  };

  async function handleSave() {
    if (!selectedAssetIds.length) {
      // Only show error if there are no screenshots in the list
      if (required && listItems.length === 0) {
        form.setError("screenshots", {
          type: "manual",
          message: "This field is required",
        });
      }
      return;
    }

    const items = selectedAssetIds.map((assetId) => ({
      [relation.entityField]: entityId,
      [relation.assetField]: assetId,
    }));
    await addItemsToCollection(endPoint, items, {
      fields: ["id", `${relation.entityField}.id`, `${relation.assetField}.id`],
    });
    setSelectedAssetIds([]);

    // Hide upload input after successful save (only if there are screenshots)
    // The list will refresh and update listItems, which will update showUploadInput

    onAfterChange?.();
    invalidateAllQueries();
    eventBus.emit("refresh:list", `${endPoint}${storageKey}`);
  }

  return (
    <FormProvider {...form}>
      <div className="flex-1">
        <FormField
          control={form.control}
          name="screenshots"
          render={() => (
            <FormItem>
              <div className="mb-4">
                <FormLabel required={requiredFields.includes("screenshots")}>
                  Select files to add
                </FormLabel>
                <MultiFileInput
                  value={selectedAssetIds}
                  onChange={(ids) => {
                    setSelectedAssetIds(ids);
                    // Clear error when files are selected
                    if (ids.length > 0) {
                      form.clearErrors("screenshots");
                    }
                  }}
                  accept="image/*"
                  maxFiles={50}
                  className="w-full"
                  onSubmit={handleSave}
                />
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <List
          title={title}
          storageKey={storageKey}
          showListHeader={false}
          endPoint={endPoint}
          sortBy={"id"}
          filters={filters}
          meta={{ fields }}
          hidePagination={true}
          showSearch={false}
          perPage={10}
          renderItems={GridRenderer}
        />
      </div>
    </FormProvider>
  );
}
